import ControlViewModel from '@/ViewModels/ControlViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import PropertyGroupBoxViewModel from '@/ViewModels/Controls/PropertyGroupBoxViewModel';
import ResourceManager from '@/Management/ResourceManager';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import ImageHandler from '@/Utility/ImageHandler';
import ComponentManager from '@/Management/ComponentManager';
import { IDVGItemDTO } from "coreframetypings/dto";
import DropDownListViewModel from '@/ViewModels/Controls/DropDownListViewModel';
import ColorPickerViewModel from '@/ViewModels/Controls/ColorPickerViewModel';
import FileUploadViewModel from '@/ViewModels/Controls/FileUploadViewModel';
import DesignWizardSubViewModel from '@/ViewModels/Controls/DesignWizardSubViewModel';
import Control from '@/Controls/Control';
import { IControlViewModelConstructorParameter } from '#/CoreFrameInterfaces';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';

/**
 * Das ViewModel für den DesignWizard
 */
export default class DesignWizardViewModel extends ControlViewModel {

    private _BACKGROUND_SRC: string = $('body').css('backgroundImage');

    private readonly APPLICATION_BACKGROUND_DEFAULT_FILE: string = 'background_default.jpg';

    /**
     * Der Kontext
     */
    public context: any;

    public errorMsg: KnockoutObservable<string>;

    public value: KnockoutObservable<any> | any;

    public refreshIndicator: KnockoutObservable<any>;

    public previewControl: KnockoutObservable<any>;

    public previewViewModel: KnockoutObservable<any>;

    public previewBackgroundSrc: KnockoutObservable<string>;

    public items: KnockoutObservableArray<DesignWizardSubViewModel>;

    /**
     * Der angezeigte Fehlertext
     */
    public error: KnockoutObservable<string>;

    /**
     * Das Fehler-Label, falls das Design keine Änderungen zulässt
     */
    public label: LabelViewModel;

    /**
     * Der als JSON serialisierte Value
     */
    public definition: KnockoutComputed<string>;

    /*
     * Ein Observable zum Aktualisieren der Factories
     */
    public refresh: KnockoutComputed<any>;

    /**
     * Die PropertyGroupBox
     */
    public propertyGroupBox: PropertyGroupBoxViewModel;

    public backgroundSrcForReloadCache: string;

    constructor(protected params: any) {
        super(params);
        this.context = this.paramOrDefault(params.context, null);
        this.errorMsg = this.observableOrDefault(null);
        this.value = this.observableOrDefault(params.value, {});
        this.refreshIndicator = this.observableOrDefault(new Date()).extend({ rateLimit: { timeout: 400, method: "notifyWhenChangesStop" } });
        this.backgroundSrcForReloadCache = '';
        this.defineSubscription(this.refreshIndicator, (newVal) => {
            const newValues = this.getNewValuesFromItems();

            this.changeValueWhenDifferent(this.value(), newValues);
        });
        this.previewControl = this.observableOrDefault(null, null);
        this.previewViewModel = this.observableOrDefault(null, null);
        this.previewBackgroundSrc = this.observableOrDefault(null, this._BACKGROUND_SRC);
        this.items = this.observableOrDefault([]) as KnockoutObservableArray<DesignWizardSubViewModel>;
        this.error = this.observableOrDefault(params.error, null);
        this.label = this.createLabel();
        this.definition = this.createDefinition()

        this.refresh = this.definePureComputed({
            read: () => {
                return null;
            },
            write: (value) => {
                this.resetValuesToDefault();
            }
        });


        this.propertyGroupBox = this.createPropertyGroupBox();
    }

    protected createLabel(): LabelViewModel {
        return new LabelViewModelBuilder()
            .withCaption(this.error)
            .withIsVisible(this.defineComputed(() => this.error() != null))
            .build();
    }

    protected createDefinition(): KnockoutComputed<string> {
        return this.definePureComputed({
            read: () => {
                const param = {};
                const value = this.value();

                for (let i in value) {
                    if (value.hasOwnProperty(i)) {
                        param[i] = value[i] || 'FFFFFF'
                    }
                }

                const json = JSON.stringify(param);

                return json;
            },
            write: (jsonString: string) => {
                if (this.items().length == 0) {
                    const definition = JSON.parse(jsonString);
                    if (definition.hasOwnProperty('Success') && !definition.Success) {
                        if (definition.hasOwnProperty('msg')) {
                            this.errorMsg(definition.msg);
                        }
                        else {
                            this.errorMsg("Unknown error.");
                        }
                        return;
                    }

                    for (const i in definition.styles.colors) {
                        const color = definition.styles.colors[i];
                        this.items.push(this.createSubViewModel('ColorPicker', color, definition.values));
                    }
                    for (const i in definition.styles.fontsizes) {
                        const fontsize = definition.styles.fontsizes[i];
                        this.items.push(this.createSubViewModel('FontsizePicker', fontsize, definition.values));
                    }
                    for (const i in definition.styles.fonts) {
                        const font = definition.styles.fonts[i];
                        this.items.push(this.createSubViewModel('FontPicker', font, definition.values));
                    }
                    for (const i in definition.styles.imagesizes) {
                        const imagesize = definition.styles.imagesizes[i];
                        this.items.push(this.createSubViewModel('ImagesizePicker', imagesize, definition.values));
                    }
                    for (const i in definition.styles.images) {
                        const image = definition.styles.images[i];
                        this.items.push(this.createSubViewModel('ImagePicker', image, definition.values));
                    }

                    const vmParams = {};
                    for (const i in this.items()) {
                        const item = this.items()[i];
                        vmParams[item.key] = item.value;
                    }
                    vmParams['BackgroundImage'] = this.previewBackgroundSrc;
                    this.backgroundSrcForReloadCache = this.previewBackgroundSrc();
                    if (definition.previewControl)
                        this.setPreviewControl(definition.previewControl.control, definition.previewControl.viewModel, vmParams);
                    this.changeValueWhenDifferent({}, definition.values);
                }

            }
        }, this.params.definition);
    }

    protected createPropertyGroupBox(): PropertyGroupBoxViewModel {
        const designWizardOrder = [
            'ActiveBackgroundBrush',
            'PassiveBackgroundBrush',
            'ApplicationFont',
            'Background',
            'BackgroundStretch',
            'LoginBackground',
            'LoginBackgroundStretch'
        ]
        const items = this.items();
        const itemsResult = [];

        for (const i of designWizardOrder) {
            const item = items.filter((x) => x.key === i)[0];
            if (item) {
                let newDVI = this.createDVItem(item, '');
                if (newDVI)
                    itemsResult.push(newDVI);
            }
        }

        return new PropertyGroupBoxViewModel({
            layout: 'TwoColumnLayout',
            context: this.context,
            detailViewItems: itemsResult
        });
    }

    private createSubViewModel(control: string, style: { keyName: string, options: any, defaultValue: string, label: string }, values: string[]): DesignWizardSubViewModel {
        let initValue = null;
        if (!!values[style.keyName]) {
            initValue = values[style.keyName];
        }
        let options = null;
        if (!!style.options && style.options.length > 0) {
            if (typeof style.options[0] === 'object') {
                for (const i in style.options) {
                    style.options[i].label = ResourceManager.getInstance().get(style.options[i].label);
                }
                options = style.options;
            }
            else {
                options = style.options;
            }
        }

        const designWizardSubVM = ComponentManager.getViewModel<typeof DesignWizardSubViewModel>('DesignWizardSubViewModel');

        const vm = new designWizardSubVM({
            parentLocator: this.locator,
            locator: style.keyName,
            control: control,
            ergName: style.label,
            key: style.keyName,
            value: initValue,
            refresh: this.refreshIndicator,
            defaultValue: style.defaultValue,
            options: options
        });

        return vm;
    }

    private setPreviewControl(control: string, viewmodel: string, vmParams: IControlViewModelConstructorParameter): void {
        const PreviewViewModel = ComponentManager.getViewModel<typeof ControlViewModel>(viewmodel);
        const PreviewControl = ComponentManager.getControl<typeof Control>(control);

        if (PreviewControl != null && PreviewControl != null) {
            this.previewControl(control);
            const vm = new PreviewViewModel(vmParams);
            this.previewViewModel(vm);
        }
        else {
            console.log("PreviewControl kann nicht angezeigt werden.");
        }
    };

    private resetValuesToDefault(): JQueryPromise<void> {
        const items = this.items();

        for (const item of items) {
            // Auch wenn der aktuelle Wert schon dem Default entspricht muss der Wert trotzdem neu gesetzt werden
            if (item.value() !== item.defaultValue) {
                item.value(item.defaultValue);
            }
            else {
                item.value(null);
                item.value(item.defaultValue);
            }
        }

        return this.restoreBackgrounds();
    }

    private restoreBackgrounds(): JQueryPromise<void> {
        return RADEnvironmentProxy.getInstance().callOperationFromDVContext({
            detailViewContextID: this.context.id,
            operationName: 'RestoreImagesHTML',
            operationParameters: [],
            property_values: JSON.stringify({}),
            contextParameters: {},
            nestedContextName: null,
            withValidation: false
        }).then((operationResult) => {
            if (this.validateStatus(operationResult)) {
                this.previewBackgroundSrc('url(\'' + ImageHandler.resolve(this.APPLICATION_BACKGROUND_DEFAULT_FILE) + '\')');
            }
        }).promise();
    }

    private restoreBackground(): JQueryPromise<void> {
        return RADEnvironmentProxy.getInstance().callOperationFromDVContext({
            detailViewContextID: this.context.id,
            operationName: 'RestoreBackgroundHTML',
            operationParameters: [],
            property_values: JSON.stringify({}),
            contextParameters: {},
            nestedContextName: null,
            withValidation: false
        }).then((operationResult) => {
            if (this.validateStatus(operationResult)) {
                this.previewBackgroundSrc('url(\'' + ImageHandler.resolve(this.APPLICATION_BACKGROUND_DEFAULT_FILE) + '\')');
            }
        }).promise();
    }

    private restoreLoginBackground(): JQueryPromise<void> {
        return RADEnvironmentProxy.getInstance().callOperationFromDVContext({
            detailViewContextID: this.context.id,
            operationName: 'RestoreLoginBackgroundHTML',
            operationParameters: [],
            property_values: JSON.stringify({}),
            contextParameters: {},
            nestedContextName: null,
            withValidation: false
        }).then((operationResult) => { }).promise();
    }

    protected createDVItem(item: DesignWizardSubViewModel, neighborOf: string): Partial<IDVGItemDTO> {
        const dropDownListViewModel = ComponentManager.getViewModel<typeof DropDownListViewModel>('DropDownListViewModel');
        const colorPickerViewModel = ComponentManager.getViewModel<typeof ColorPickerViewModel>('ColorPickerViewModel');
        const fileUploadViewModel = ComponentManager.getViewModel<typeof FileUploadViewModel>('FileUploadViewModel');

        let ret: Partial<IDVGItemDTO> = null;
        if (item.control === "ColorPicker") {
            ret = {
                ergName: item.ergName,
                uiGenerator: 'ColorPicker',
                required: false,
                viewModel: new colorPickerViewModel({
                    parentLocator: this.locator,
                    locator: item.locator,
                    value: item.value,
                    isEnabled: this.isEnabled
                })
            };
        }
        else if (item.control === "FontPicker") {
            ret = {
                ergName: item.ergName,
                uiGenerator: 'DropDownList',
                required: false,
                viewModel: new dropDownListViewModel({
                    parentLocator: this.locator,
                    locator: item.locator,
                    data: item.options,
                    value: item.value,
                    template: '<span style="font-family: \'#:data.value#\'">#:data.label#</span>',
                    valueTemplate: '<span style="font-family: \'#:data.value#\'">#:data.label#</span>',
                    dataTextField: 'label',
                    dataValueField: 'value',
                    isEnabled: this.isEnabled
                })
            };
        }
        else if (item.control === "FontsizePicker") {
            ret = {
                ergName: item.ergName,
                uiGenerator: 'DropDownList',
                required: false,
                viewModel: new dropDownListViewModel({
                    parentLocator: this.locator,
                    locator: item.locator,
                    data: item.options,
                    value: item.value,
                    dataTextField: 'label',
                    dataValueField: 'value',
                    isEnabled: this.isEnabled
                })
            };
        }
        else if (item.control === "ImagePicker") {
            if (item.key === 'LoginBackground') {
                const vm = new fileUploadViewModel({
                    parentLocator: this.locator,
                    locator: "loginbackgroundPicker",
                    previewMode: 'None',
                    previewEnabled: false,
                    loadOperation: 'LoadLoginBackgroundHTML',
                    isEnabled: this.defineComputed(() => {
                        return this.isEnabled() && !this.context.isLocked();
                    }),
                    downloadEnabled: this.defineComputed(() => {
                        return !this.context.isLocked();
                    }),
                    fileFilter: '.png,.jpg',
                    name: this.getBackgroundFilename('LoginBackgroundImage'),
                    onChange: (file) => {
                        this.context.genericObject.isModified(true);

                        if (file != null) {

                            RADEnvironmentProxy.getInstance().callOperationFromDVContext({
                                detailViewContextID: this.context.id,
                                operationName: 'UpdateLoginBackgroundHTML',
                                operationParameters: [file.name, file.techName, ''],
                                property_values: JSON.stringify({}),
                                contextParameters: {},
                                nestedContextName: null,
                                withValidation: false
                            }).then((operationResult) => {
                                if (this.validateStatus(operationResult)) {
                                }
                                else {
                                    vm.file(null);
                                    vm.preview.source(null);
                                }
                            });
                        }
                        else {
                            this.restoreLoginBackground();
                        }

                    }
                });

                ret = {
                    ergName: item.ergName,
                    uiGenerator: 'FileUpload',
                    required: false,
                    viewModel: vm
                };
            }
            else {
                const vm = new fileUploadViewModel({
                    parentLocator: this.locator,
                    locator: "backgroundPicker",
                    previewMode: 'None',
                    previewEnabled: false,
                    loadOperation: 'LoadBackgroundHTML',
                    isEnabled: this.defineComputed(() => {
                        return this.isEnabled() && !this.context.isLocked();
                    }),
                    downloadEnabled: this.defineComputed(() => {
                        return !this.context.isLocked();
                    }),
                    fileFilter: '.png,.jpg',
                    name: this.getBackgroundFilename('ApplicationBackgroundImage'),
                    onChange: (file) => {
                        this.context.genericObject.isModified(true);

                        if (file != null) {

                            RADEnvironmentProxy.getInstance().callOperationFromDVContext({
                                detailViewContextID: this.context.id,
                                operationName: 'UpdateBackgroundHTML',
                                operationParameters: [file.name, file.techName, ''],
                                property_values: JSON.stringify({}),
                                contextParameters: {},
                                nestedContextName: null,
                                withValidation: false
                            }).then((operationResult) => {
                                if (this.validateStatus(operationResult)) {
                                    this.updatePreviewBackgroundSrc(vm.getPreviewUrl());
                                }
                                else {
                                    vm.file(null);
                                    vm.preview.source(null);
                                }
                            });
                        }
                        else {
                            this.restoreBackground();
                        }

                    }
                });

                ret = {
                    ergName: item.ergName,
                    uiGenerator: 'FileUpload',
                    required: false,
                    viewModel: vm
                };
            }
        }
        else if (item.control === "ImagesizePicker") {
            ret = {
                ergName: item.ergName,
                uiGenerator: 'DropDownList',
                required: false,
                viewModel: new dropDownListViewModel({
                    parentLocator: this.locator,
                    locator: item.locator,
                    data: item.options,
                    value: item.value,
                    dataTextField: 'label',
                    dataValueField: 'value',
                    isEnabled: this.isEnabled
                })
            };
        }

        ret.name = item.key;
        if (neighborOf != '')
            ret.uiGenerator = ret.uiGenerator + '?NeighborOf=' + neighborOf;

        return ret;
    }

    private updatePreviewBackgroundSrc(newURL: string): void {
        let url = newURL;
        if (url == null) {
            url = 'url(\'\')';
        }
        else {
            url = 'url(\'' + url + '\')';
        }
        this.previewBackgroundSrc(url);
    }

    private changeValueWhenDifferent(old: any, newval: any): void {
        const oldString = JSON.stringify(ko.toJS(old));
        const newString = JSON.stringify(ko.toJS(newval));

        if (oldString !== newString) {
            this.value(newval);
        }
    }

    private getBackgroundFilename(keyName: string): JQueryPromise<string> {
        return RADEnvironmentProxy.getInstance().callOperationFromDVContext({
            detailViewContextID: this.context.id,
            operationName: 'GetBackgroundFilename',
            operationParameters: [keyName],
            property_values: JSON.stringify({}),
            contextParameters: {},
            nestedContextName: null,
            withValidation: false,
            needsLock: false
        }).then((operationResult) => {
            if (this.validateStatus(operationResult)) {
                return operationResult.returnValue
            }
        }).promise();
    }

    private getNewValuesFromItems(): Record<string, any> {
        const values = {};
        const items = this.items();

        for (const item of items) {
            if (item.control != 'ImagePicker' && item.value() !== item.defaultValue) {
                values[item.key] = item.value();
            }
        }
        return values;
    }
}

ComponentManager.setViewModel('DesignWizardViewModel', DesignWizardViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');